<template>
  <!-- Account-menu item (styled by LhsHeaderCard's .lhs-header-banner__menu-item
       rule; this lives in that popover's menu-items slot). Opens the report
       modal. We deliberately do NOT close the account popover on open: this
       component instance lives inside the popover's v-if, so closing it would
       unmount the modal too. The modal backdrop covers the popover; both are
       dismissed together when the modal closes. -->
  <button type="button" class="lhs-header-banner__menu-item" @click="openModal">
    <CygnusIcon name="flag" class="me-2" /> Report an issue
  </button>

  <teleport to="body">
    <div v-if="modalOpen" class="report-issue__backdrop" @click.self="cancel">
      <div class="report-issue__panel" role="dialog" aria-modal="true" aria-labelledby="report-issue-title">
        <button type="button" class="report-issue__close" aria-label="Close" @click="cancel">
          <CygnusIcon name="cross" />
        </button>

        <!-- Success state -->
        <div v-if="sent" class="report-issue__done">
          <div class="report-issue__done-icon"><CygnusIcon name="check-circle" /></div>
          <h3 class="report-issue__heading">Report sent</h3>
          <p class="report-issue__done-text">Thanks - your report has been sent to the team.</p>
          <div class="report-issue__actions">
            <button type="button" class="report-issue__btn report-issue__btn--primary" @click="done">Close</button>
          </div>
        </div>

        <!-- Form state -->
        <form v-else @submit.prevent="submit">
          <h3 id="report-issue-title" class="report-issue__heading">Report an issue</h3>

          <label class="report-issue__label" for="report-issue-title-input">Title</label>
          <input
            id="report-issue-title-input"
            ref="titleInput"
            v-model="title"
            type="text"
            class="report-issue__input"
            maxlength="255"
            placeholder="A short summary of the problem"
            :disabled="submitting"
          />

          <label class="report-issue__label" for="report-issue-desc">Description</label>
          <textarea
            id="report-issue-desc"
            v-model="description"
            class="report-issue__input report-issue__textarea"
            rows="5"
            placeholder="What happened? What did you expect?"
            :disabled="submitting"
          ></textarea>

          <label class="report-issue__label" for="report-issue-severity">Severity</label>
          <select id="report-issue-severity" v-model="severity" class="report-issue__input" :disabled="submitting">
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>

          <p class="report-issue__context-note">
            We include the page you were on and your app version to help us debug.
          </p>

          <p v-if="error" class="report-issue__error">{{ error }}</p>

          <div class="report-issue__actions">
            <button type="button" class="report-issue__btn" :disabled="submitting" @click="cancel">Cancel</button>
            <button type="submit" class="report-issue__btn report-issue__btn--primary" :disabled="submitting || !title.trim()">
              {{ submitting ? 'Sending...' : 'Send report' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </teleport>
</template>

<script>
/* global BUILD_NUMBER */
/**
 * ReportIssueMenuItem — the shell-wide "Report an issue" affordance. Rendered
 * in LhsHeaderCard's account-menu `menu-items` slot, so it is reachable from
 * EVERY page for EVERY user (no permission gating — everyone can report).
 *
 * Opens a modal (title / description / severity + a context note) and POSTs to
 * /api/v1/me/report-issue. Auto-includes the page URL, current route and app
 * version so the team can debug. The user experience is identical whether or
 * not the platform has Town Centre configured — the endpoint always records
 * the report and the async job handles delivery.
 */
import useApi from '@apexl/icarus-core/src/services/api';

export default {
  name: 'ReportIssueMenuItem',
  props: {
    /** Supplied by LhsHeaderCard's menu-items slot — dismisses the account popover. */
    closeMenu: { type: Function, default: () => {} },
  },
  data() {
    return {
      modalOpen: false,
      title: '',
      description: '',
      severity: 'medium',
      submitting: false,
      sent: false,
      error: '',
    };
  },
  methods: {
    openModal() {
      this.reset();
      this.modalOpen = true;
      this.$nextTick(() => { this.$refs.titleInput?.focus(); });
    },
    reset() {
      this.title = '';
      this.description = '';
      this.severity = 'medium';
      this.submitting = false;
      this.sent = false;
      this.error = '';
    },
    cancel() {
      if (this.submitting) return;
      this.modalOpen = false;
      // Tidy up: also close the account popover behind the modal.
      this.closeMenu();
    },
    done() {
      this.modalOpen = false;
      this.closeMenu();
    },
    async submit() {
      const title = this.title.trim();
      if (!title || this.submitting) return;
      this.submitting = true;
      this.error = '';
      try {
        await useApi().post('/api/v1/me/report-issue', {
          title,
          body: this.description.trim(),
          severity: this.severity,
          source_url: (typeof window !== 'undefined' && window.location) ? window.location.href : '',
          route: this.$route?.path || '',
          app_version: (typeof BUILD_NUMBER !== 'undefined' && BUILD_NUMBER) ? BUILD_NUMBER : '',
        });
        this.sent = true;
      } catch (e) {
        // Never leak backend/config detail — a generic, honest message.
        this.error = 'Sorry, we could not send your report. Please try again.';
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="scss">
/* Not scoped — <teleport to="body"> moves the modal out of this component's
   tree. Class names are namespaced (report-issue__*). */
.report-issue__backdrop {
  position: fixed; inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex; align-items: center; justify-content: center;
  z-index: 3000; /* above the account popover (2040/2050) */
  padding: 1rem;
}
.report-issue__panel {
  position: relative;
  background: var(--theme-bg-card, #fff);
  color: var(--theme-text-primary, #2A2B2E);
  border: 1px solid var(--theme-border, #E8E3DC);
  border-radius: var(--theme-radius-lg, 10px);
  box-shadow: var(--theme-shadow-panel, 0 12px 40px rgba(0, 0, 0, 0.28));
  width: 100%; max-width: 460px;
  padding: 1.4rem 1.5rem 1.25rem;
}
.report-issue__close {
  position: absolute; top: 0.6rem; right: 0.6rem;
  width: 30px; height: 30px; padding: 0;
  border: 0; background: transparent; cursor: pointer;
  color: var(--theme-text-muted, #7a736a);
  border-radius: 6px;
  display: inline-flex; align-items: center; justify-content: center;
  &:hover { background: var(--theme-accent-light, #F5F1E9); color: var(--theme-text-primary, #2A2B2E); }
}
.report-issue__heading {
  margin: 0 0 1rem;
  font-size: 1.15rem; font-weight: 700;
  color: var(--theme-text-primary, #2A2B2E);
}
.report-issue__label {
  display: block;
  margin: 0.75rem 0 0.3rem;
  font-size: 0.78rem; font-weight: 600;
  color: var(--theme-text-secondary, #5b544c);
  letter-spacing: 0.01em;
}
.report-issue__input {
  width: 100%;
  padding: 0.55rem 0.7rem;
  font-size: 0.9rem; font-family: inherit;
  color: var(--theme-text-primary, #2A2B2E);
  background: var(--theme-bg-input, #fff);
  border: 1px solid var(--theme-border, #E8E3DC);
  border-radius: var(--theme-radius, 6px);
  box-sizing: border-box;
  &:focus { outline: none; border-color: var(--theme-accent-primary, #8E7456); box-shadow: 0 0 0 3px rgba(142, 116, 86, 0.18); }
  &:disabled { opacity: 0.6; }
}
.report-issue__textarea { resize: vertical; min-height: 90px; }
.report-issue__context-note {
  margin: 0.8rem 0 0;
  font-size: 0.76rem; line-height: 1.4;
  color: var(--theme-text-muted, #7a736a);
}
.report-issue__error {
  margin: 0.75rem 0 0;
  font-size: 0.82rem;
  color: var(--theme-danger, #b3261e);
}
.report-issue__actions {
  display: flex; justify-content: flex-end; gap: 0.6rem;
  margin-top: 1.2rem;
}
.report-issue__btn {
  padding: 0.5rem 1rem;
  font-size: 0.85rem; font-weight: 600; font-family: inherit;
  border-radius: var(--theme-radius, 6px);
  border: 1px solid var(--theme-border, #E8E3DC);
  background: transparent;
  color: var(--theme-text-primary, #2A2B2E);
  cursor: pointer;
  &:hover:not(:disabled) { background: var(--theme-accent-light, #F5F1E9); }
  &:disabled { opacity: 0.55; cursor: default; }
  &--primary {
    background: var(--theme-accent-primary, #8E7456);
    border-color: var(--theme-accent-primary, #8E7456);
    color: #fff;
    &:hover:not(:disabled) { background: var(--theme-accent-primary-hover, #7a6349); }
  }
}
.report-issue__done { text-align: center; padding: 0.5rem 0 0.25rem; }
.report-issue__done-icon {
  font-size: 2.4rem; line-height: 1;
  color: var(--theme-success, #2e7d5b);
  margin-bottom: 0.6rem;
}
.report-issue__done-text {
  margin: 0;
  font-size: 0.92rem;
  color: var(--theme-text-secondary, #5b544c);
}
</style>
